import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../services/api';
import { format } from 'date-fns';
import { PhoneCall, PhoneMissed, PhoneOff, Download, Search, Filter, List, Clock } from 'lucide-react';

const DISPOSITION_CONFIG = {
  'ANSWERED': { label: 'Contestada', cls: 'badge-success', icon: PhoneCall },
  'NO ANSWER': { label: 'Perdida', cls: 'badge-warning', icon: PhoneMissed },
  'BUSY': { label: 'Ocupado', cls: 'badge-danger', icon: PhoneOff },
  'FAILED': { label: 'Fallida', cls: 'badge-danger', icon: PhoneOff },
};

function formatDur(secs) {
  const m = Math.floor(secs / 60); const s = secs % 60;
  return m > 0 ? m + 'm ' + s + 's' : s + 's';
}

export default function CDRPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ src: '', dst: '', disposition: '', startDate: '', endDate: '', type: '' });

  const params = new URLSearchParams({ page, limit: 50, ...Object.fromEntries(Object.entries(filters).filter(([,v]) => v)) });

  const { data, isLoading } = useQuery(
    ['cdr', page, filters],
    () => api.get('/cdr?' + params).then(r => r.data),
    { keepPreviousData: true }
  );

  const { data: summary } = useQuery(
    ['cdr-summary', filters.startDate, filters.endDate],
    () => api.get('/cdr/summary?startDate=' + (filters.startDate || new Date().toISOString().split('T')[0])).then(r => r.data)
  );

  const handleExport = () => {
    window.open('/api/cdr/export?' + params + '&format=csv', '_blank');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Registro de Llamadas (CDR)</h1>
          <p className="text-gray-400 text-sm">{data?.total || 0} registros</p>
        </div>
        <button onClick={handleExport} className="btn-secondary">
          <Download size={16} /> Exportar CSV
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              label: 'Total', value: summary.total, cls: 'text-white', Icon: List,
              sub: 'llamadas registradas',
            },
            {
              label: 'Contestadas', value: summary.answered, cls: 'text-green-400', Icon: PhoneCall,
              sub: summary.total ? `${Math.round((summary.answered / summary.total) * 100)}% del total` : 'sin datos',
            },
            {
              label: 'Perdidas', value: summary.no_answer, cls: 'text-yellow-400', Icon: PhoneMissed,
              sub: summary.total ? `${Math.round((summary.no_answer / summary.total) * 100)}% del total` : 'sin datos',
            },
            {
              label: 'Duración total', value: formatDur(parseInt(summary.total_billsec || 0)), cls: 'text-blue-400', Icon: Clock,
              sub: 'tiempo conversado',
            },
          ].map(s => (
            <div key={s.label}
              className="relative rounded-xl p-4 bg-gray-900/60 border border-gray-800 overflow-hidden">
              <s.Icon size={76} className="absolute -right-3 -top-3 text-white opacity-[0.04] pointer-events-none" />
              <div className="text-[11px] uppercase tracking-wider text-muted font-medium">{s.label}</div>
              <div className={`text-[34px] leading-tight font-bold mt-0.5 ${s.cls}`}
                   style={{ fontVariantNumeric: 'tabular-nums' }}>
                {s.value}
              </div>
              <div className="flex items-center gap-1.5 mt-2 text-xs text-muted">
                <s.Icon size={12} className="shrink-0" />
                <span className="truncate">{s.sub}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <input aria-label="Origen" value={filters.src} onChange={e => setFilters(f => ({ ...f, src: e.target.value }))}
            className="input" placeholder="Origen..." />
          <input aria-label="Destino" value={filters.dst} onChange={e => setFilters(f => ({ ...f, dst: e.target.value }))}
            className="input" placeholder="Destino..." />
          <select aria-label="Estado" value={filters.disposition} onChange={e => setFilters(f => ({ ...f, disposition: e.target.value }))} className="input">
            <option value="">Todos los estados</option>
            <option value="ANSWERED">Contestada</option>
            <option value="NO ANSWER">Perdida</option>
            <option value="BUSY">Ocupado</option>
          </select>
          <select aria-label="Tipo" value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} className="input">
            <option value="">Todos los tipos</option>
            <option value="inbound">Entrante</option>
            <option value="outbound">Saliente</option>
            <option value="internal">Interna</option>
          </select>
          <input aria-label="Fecha inicio" type="date" value={filters.startDate} onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))} className="input" />
          <input aria-label="Fecha fin" type="date" value={filters.endDate} onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))} className="input" />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                {['Fecha', 'Origen', 'Destino', 'Duracion', 'Estado', 'Tipo'].map(h => (
                  <th key={h} className="table-th">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(data?.data || []).map(row => {
                  const disp = DISPOSITION_CONFIG[row.disposition] || { label: row.disposition, cls: 'badge-info', icon: PhoneCall };
                  const Icon = disp.icon;
                  return (
                    <tr key={row.id + row.uniqueid} className="table-row">
                      <td className="table-td text-xs">{format(new Date(row.calldate), 'dd/MM/yy HH:mm:ss')}</td>
                      <td className="table-td font-mono text-blue-400">{row.src}</td>
                      <td className="table-td font-mono text-gray-300">{row.dst}</td>
                      <td className="table-td">{formatDur(row.billsec)}</td>
                      <td className="table-td">
                        <span className={disp.cls + ' flex items-center gap-1 w-fit'}>
                          <Icon size={11} /> {disp.label}
                        </span>
                      </td>
                      <td className="table-td text-xs text-muted">{row.userfield || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data?.total > 50 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
                <span className="text-sm text-gray-400">Pagina {page} de {Math.ceil(data.total / 50)}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="btn-secondary py-1">Anterior</button>
                  <button onClick={() => setPage(p => p+1)} disabled={page >= Math.ceil(data.total / 50)} className="btn-secondary py-1">Siguiente</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
